import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GIS_LAYER_SEARCH } from '@rsos/capstone/src/constants/capabilities';
import useHasCapability from '@rsos/hooks/useHasCapability';
import styles from './GlobalMapSearch.module.css';
import {
  ClearButton,
  Input,
  SearchBar,
  SearchIcon,
  Wrapper,
} from './GlobalMapSearch.styles';
import RegularSearchExperience, {
  type RegularExperienceHandle,
} from './RegularSearchExperience';
import SlashCommandExperience, {
  type SlashExperienceHandle,
} from './SlashCommandExperience';

/**
 * Thin shell that owns the input, clear/search buttons, and focus
 * lifecycle, then routes typing to one of two always-mounted sibling
 * experiences based on whether the input starts with `/`. Keeping both
 * mounted preserves each workflow's state across a round-trip through
 * the other.
 */
const GlobalMapSearch: React.FC = () => {
  const { hasCapability } = useHasCapability(GIS_LAYER_SEARCH);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  // Bubbled up from the regular experience so the trailing Search button
  // can disable itself while a request is in flight.
  const [isSearchingForButton, setIsSearchingForButton] = useState(false);
  // Bubbled up from the slash experience: the suffix that, when appended
  // to `searchTerm`, equals what Tab will autofill. Rendered as inline
  // ghost text directly inside the search field.
  const [ghostSuffix, setGhostSuffix] = useState('');

  const regularRef = useRef<RegularExperienceHandle>(null);
  const slashRef = useRef<SlashExperienceHandle>(null);

  const inputMode: 'search' | 'command' = searchTerm.startsWith('/')
    ? 'command'
    : 'search';

  useEffect(() => {
    const el = document.getElementById('navbar-center-slot');
    if (el) setPortalTarget(el);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        regularRef.current?.onClose();
        setIsFocused(false);
        isFocusedRef.current = false;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // `preserveInput` is set by scoped-result selections (`/transcript`,
  // `/chat`, `/parcel` rows) so picking a hit doesn't wipe the user's
  // query — matching the regular result-click path. Action commands and
  // arg picks still clear since their input was a one-shot directive.
  const handleSlashComplete = useCallback(
    (opts?: { preserveInput?: boolean }) => {
      if (!opts?.preserveInput) setSearchTerm('');
      regularRef.current?.onClose();
      isFocusedRef.current = false;
      inputRef.current?.blur();
    },
    [],
  );

  const handleClear = useCallback(() => {
    setSearchTerm('');
    regularRef.current?.onClear();
    slashRef.current?.onClear();
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setSearchTerm(next);
    // Regular experience owns the wipe (clear results, abort in-flight,
    // drop pinned popover). No-ops on slash inputs.
    regularRef.current?.onChange(next);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Re-derive the routing target inside the callback so it tracks
      // `searchTerm` — closing over a render-time const would freeze
      // routing to whichever ref was active on first render and break
      // Tab autocomplete after switching modes.
      const ref = searchTerm.startsWith('/') ? slashRef : regularRef;
      ref.current?.onKeyDown(e);
    },
    [searchTerm],
  );

  if (!hasCapability || !portalTarget) return null;

  const canSubmitSearch = searchTerm.trim().length >= 1;

  return createPortal(
    <Wrapper ref={wrapperRef}>
      <div className={styles.searchRow}>
        <div className={styles.searchBarSlot}>
          <SearchBar>
            <SearchIcon
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </SearchIcon>
            <div className={styles.inputWrap}>
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search (calls, places, responders, layers...)"
                value={searchTerm}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  isFocusedRef.current = true;
                  setIsFocused(true);
                  regularRef.current?.onFocus();
                  slashRef.current?.onFocus();
                }}
                onBlur={() => {
                  isFocusedRef.current = false;
                  setIsFocused(false);
                }}
              />
              {ghostSuffix && (
                <div className={styles.ghost} aria-hidden>
                  <span className={styles.ghostInvisible}>{searchTerm}</span>
                  <span>{ghostSuffix}</span>
                </div>
              )}
            </div>
            {searchTerm && (
              <ClearButton onClick={handleClear} aria-label="Clear search">
                ×
              </ClearButton>
            )}
          </SearchBar>

          <RegularSearchExperience
            ref={regularRef}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isHidden={inputMode === 'command'}
            isFocused={isFocused}
            isFocusedRef={isFocusedRef}
            inputRef={inputRef}
            onIsSearchingChange={setIsSearchingForButton}
          />
          <SlashCommandExperience
            ref={slashRef}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isHidden={inputMode === 'search' || !isFocused}
            inputRef={inputRef}
            isFocusedRef={isFocusedRef}
            onComplete={handleSlashComplete}
            onGhostSuffixChange={setGhostSuffix}
          />
        </div>
        <button
          type="button"
          className={styles.searchButton}
          onClick={() => regularRef.current?.triggerSearch()}
          // Preserve input focus on click so the dropdown doesn't briefly
          // close and re-open.
          onMouseDown={e => e.preventDefault()}
          disabled={!canSubmitSearch || isSearchingForButton}
          aria-label="Run search"
        >
          Search
        </button>
      </div>
    </Wrapper>,
    portalTarget,
  );
};

export default GlobalMapSearch;
